import React, { useEffect, PropsWithChildren } from 'react';
import { Drawer as AntDrawer } from 'antd';
import { clsx } from 'clsx';

import { Colors, FontSize, LineHeight } from '@common/tokens';
import { Placeholder } from '@common/components/placeholder';
import { hideBodyScroll, showBodyScroll } from '@common/services';
import { DrawerLoadingSkeleton } from './_drawer-loading-skeleton';

const availableSizes = { sm: 600, lg: 750 };

export type DrawerProps = {
  open: boolean;
  className?: string;
  title?: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'sm' | 'lg';
  bodyStyle?: React.CSSProperties;
  loading?: boolean;
  onClose?: () => void;
};

/**
 * Prefer wrapping Drawers with a feature component and registering it as a
 * Global Dialog in the _dialogs-map.ts file. Read more in dialogs-readme.md
 */

export function Drawer({
  open,
  className,
  title,
  footer,
  children,
  size,
  bodyStyle,
  loading,
  onClose = () => {},
}: PropsWithChildren<DrawerProps>) {
  const width = availableSizes[size || 'sm'];
  const shouldRenderLoadingTitle = loading && !title;
  const drawerTitle = shouldRenderLoadingTitle ? (
    <div data-testid="wk-drawer-loading-title-skeleton">
      <Placeholder.Input loading size="small" width={300}>
        <div />
      </Placeholder.Input>
    </div>
  ) : (
    title
  );

  useEffect(() => showBodyScroll, []);

  useEffect(() => {
    if (open) {
      hideBodyScroll();
    } else {
      showBodyScroll();
    }
  }, [open]);

  return (
    <>
      <AntDrawer
        destroyOnClose
        data-testid="wk-drawer"
        title={drawerTitle}
        footer={footer}
        className={clsx('wk-drawer', className)}
        onClose={onClose}
        open={open}
        width={width}
        bodyStyle={bodyStyle}
      >
        {loading ? <DrawerLoadingSkeleton /> : children}
      </AntDrawer>
      <style jsx>{`
        :global(.wk-drawer .ant-drawer-title) {
          font-weight: 400;
          font-size: ${FontSize.md};
          line-height: ${LineHeight.xl};
          color: ${Colors.textDefault};
        }
      `}</style>
    </>
  );
}
